import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { errorMessage } from '../../lib/api';
import {
  businessActions,
  lossReasons as lossReasonsApi,
  type BusinessAction,
  type BusinessTarget,
} from '../../lib/resources';
import type { BulkReport, Stage } from '../../types';
import { Button, ErrorNote, Field, Modal, inputClass } from '../ui';

const TITLES: Record<BusinessAction, string> = {
  move: 'Mover negócios',
  win: 'Marcar como ganhos',
  lose: 'Marcar como perdidos',
  restore: 'Restaurar status',
  delete: 'Excluir negócios',
};

const VERBS: Record<BusinessAction, string> = {
  move: 'Mover',
  win: 'Ganhar',
  lose: 'Perder',
  restore: 'Restaurar',
  delete: 'Excluir',
};

/**
 * Confirmação de ação em lote (decisão do plano): mostra a contagem real antes
 * de agir — quantos negócios, quantos a regra da etapa vai barrar e quantas
 * atividades automáticas serão criadas. Depois, o relatório do que ficou de fora.
 */
export function BusinessBulkDialog({
  action,
  target,
  scopeLabel,
  stages,
  defaultStageId,
  onClose,
  onDone,
}: {
  action: BusinessAction;
  target: BusinessTarget;
  /** "selecionados", "da etapa X", "do filtro atual"… */
  scopeLabel: string;
  stages: Stage[];
  defaultStageId?: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [destination, setDestination] = useState(defaultStageId ?? stages[0]?.id ?? '');
  const [reasonId, setReasonId] = useState('');
  const [justification, setJustification] = useState('');
  const [report, setReport] = useState<BulkReport | null>(null);

  const preview = useQuery({
    queryKey: ['business-preview', action, target, destination],
    queryFn: () =>
      businessActions.preview(action, target, action === 'move' ? destination : undefined),
    enabled: action !== 'move' || Boolean(destination),
  });
  const reasons = useQuery({
    queryKey: ['loss-reasons'],
    queryFn: () => lossReasonsApi.list(),
    enabled: action === 'lose',
  });
  const reason = (reasons.data?.data ?? []).find((item) => item.id === reasonId);

  const run = useMutation({
    mutationFn: () => {
      switch (action) {
        case 'move':
          return businessActions.move(target, destination);
        case 'win':
          return businessActions.win(target);
        case 'lose':
          return businessActions.lose(target, reasonId, justification.trim() || undefined);
        case 'restore':
          return businessActions.restore(target);
        default:
          return businessActions.remove(target);
      }
    },
    onSuccess: (result) => {
      setReport(result);
      onDone();
    },
  });

  const count = preview.data?.count ?? 0;
  const blocked = preview.data?.blocked ?? 0;
  const destinationName = stages.find((stage) => stage.id === destination)?.name;
  const missingReason = action === 'lose' && !reasonId;
  const missingJustification =
    action === 'lose' && reason?.requiredJustification && !justification.trim();

  if (report) {
    return (
      <Modal title={TITLES[action]} onClose={onClose}>
        <div className="space-y-4 text-sm text-slate-600">
          <p>
            <strong>{report.affected}</strong> negócio(s) atualizado(s).
            {report.skipped.length > 0 && ` ${report.skipped.length} ficaram de fora:`}
          </p>
          {report.skipped.length > 0 && (
            <ul className="max-h-60 space-y-1 overflow-y-auto rounded-lg bg-amber-50 p-3 text-xs text-amber-800">
              {report.skipped.map((item) => (
                <li key={item.id}>
                  <strong>#{item.code}</strong> — {item.reason}
                </li>
              ))}
            </ul>
          )}
          <div className="flex justify-end">
            <Button variant="primary" onClick={onClose}>
              Fechar
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title={TITLES[action]} onClose={onClose}>
      <div className="space-y-4 text-sm text-slate-600">
        {action === 'move' && (
          <Field label="Etapa de destino *">
            <select
              className={inputClass}
              value={destination}
              onChange={(event) => setDestination(event.target.value)}
            >
              {stages.map((stage) => (
                <option key={stage.id} value={stage.id}>
                  {stage.name}
                </option>
              ))}
            </select>
          </Field>
        )}

        {action === 'lose' && (
          <>
            <Field label="Motivo da perda *">
              <select
                className={inputClass}
                value={reasonId}
                onChange={(event) => setReasonId(event.target.value)}
              >
                <option value="">Selecione…</option>
                {(reasons.data?.data ?? []).map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={reason?.requiredJustification ? 'Justificativa *' : 'Justificativa'}>
              <textarea
                rows={3}
                className={inputClass}
                value={justification}
                onChange={(event) => setJustification(event.target.value)}
              />
            </Field>
          </>
        )}

        <div className="rounded-lg bg-slate-50 p-3">
          {preview.isLoading ? (
            <p>Contando negócios…</p>
          ) : preview.error ? (
            <ErrorNote>{errorMessage(preview.error, 'Não foi possível contar')}</ErrorNote>
          ) : (
            <>
              <p>
                {VERBS[action]} <strong>{count}</strong> negócio(s) {scopeLabel}
                {action === 'move' && destinationName ? ` para "${destinationName}"` : ''}.
              </p>
              {blocked > 0 && (
                <p className="mt-1 text-amber-700">
                  {blocked} não {blocked === 1 ? 'passa' : 'passam'} pelas regras da etapa ou
                  pelas suas permissões e {blocked === 1 ? 'será pulado' : 'serão pulados'}.
                </p>
              )}
              {(preview.data?.autoActivities ?? 0) > 0 && (
                <p className="mt-1 text-sky-700">
                  A etapa de destino vai criar {preview.data?.autoActivities} atividade(s)
                  automática(s).
                </p>
              )}
              {action === 'delete' && (
                <p className="mt-1 text-xs text-slate-400">
                  Os negócios vão para a Lixeira com as atividades e podem ser restaurados.
                </p>
              )}
            </>
          )}
        </div>

        {run.error && <ErrorNote>{errorMessage(run.error, 'A ação falhou')}</ErrorNote>}

        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancelar</Button>
          <Button
            variant={action === 'delete' || action === 'lose' ? 'danger' : 'primary'}
            disabled={
              run.isPending ||
              preview.isLoading ||
              count === 0 ||
              missingReason ||
              Boolean(missingJustification)
            }
            onClick={() => run.mutate()}
          >
            {run.isPending ? 'Aguarde…' : `${VERBS[action]} ${count}`}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
